import React, { useRef, useEffect, useCallback, RefObject } from 'react';
type UseScrollInteractionsProps = {
  containerRef: RefObject<HTMLDivElement | null>;
  imageSetWidth: number;
  isPaused: boolean;
  capturePositionAndPause: () => void;
};

// target frames per second in milliseconds (1000 / 60)
const FRAME_DURATION_MS = 16.67;

// friction value that controls how quickly scrolling slows down after the user releases their drag
// so it gradually slows down instead of stopping instantly
const MOMENTUM_FRICTION = 0.95;
export const useScrollInteractions = ({
  containerRef,
  imageSetWidth,
  isPaused,
  capturePositionAndPause
}: UseScrollInteractionsProps) => {
  const isDraggingRef = useRef(false);
  // last horizontal pointer position (used to calculate drag distance)
  const lastPointerXRef = useRef(0);
  const scrollSpeedRef = useRef(0);
  const lastTimeRef = useRef(Date.now());
  const animationFrameRef = useRef<number | null>(null);
  // track the timing of the last move to calculate velocity
  const lastMoveTimestamp = useRef(0);
  useEffect(() => {
    const container = containerRef.current;

    // only run when user has interacted
    if (!isPaused || !imageSetWidth || !container) {
      return;
    }

    // initialize scroll to the start of the second set
    container.scrollLeft = imageSetWidth;
    // set initial timestamp for delta calculations
    lastTimeRef.current = Date.now();
    const animate = () => {
      const now = Date.now();
      // calculate frame delta capped at 2x
      const deltaTime = Math.min((now - lastTimeRef.current) / FRAME_DURATION_MS, 2);

      // update timestamp for the next frame
      lastTimeRef.current = now;

      // apply momentum friction when not dragging
      if (!isDraggingRef.current && Math.abs(scrollSpeedRef.current) > 0.1) {
        // move scroll based on velocity
        container.scrollLeft += scrollSpeedRef.current * deltaTime;
        // apply friction to slow down movement
        scrollSpeedRef.current *= MOMENTUM_FRICTION;
      }
      const {
        scrollLeft
      } = container;

      // if scrolling passes the right threshold
      if (scrollLeft >= imageSetWidth * 1.5) {
        // jump back to the second set
        container.scrollLeft = scrollLeft - imageSetWidth;

        // if scrolling passes the left threshold
      } else if (scrollLeft <= imageSetWidth * 0.5) {
        // jump forward to the second set
        container.scrollLeft = scrollLeft + imageSetWidth;
      }

      // schedule the next frame
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);

    // eslint-disable-next-line consistent-return
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPaused, imageSetWidth, containerRef]);
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    capturePositionAndPause();
    isDraggingRef.current = true;
    lastPointerXRef.current = e.clientX;
    lastMoveTimestamp.current = Date.now();
    scrollSpeedRef.current = 0;
    lastTimeRef.current = Date.now();

    // capture pointer to receive events even if cursor leaves element
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [capturePositionAndPause]);
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const container = containerRef.current;

    // exit if not dragging
    if (!isDraggingRef.current || !container) {
      return;
    }
    const now = Date.now();
    // time elapsed (ms) since the last pointermove event — used to compute velocity
    const timeDelta = now - lastMoveTimestamp.current;

    // calculate how far pointer moved since last event
    const delta = lastPointerXRef.current - e.clientX;
    container.scrollLeft += delta;
    if (timeDelta > 0) {
      // pixels moved per frame at the current pointer speed
      const instantVelocity = delta / timeDelta * FRAME_DURATION_MS;
      // smooth velocity using weighted average:
      // 40% previous velocity + 60% latest sample reduces jitter while
      // still reacting quickly to speed changes
      scrollSpeedRef.current = scrollSpeedRef.current * 0.4 + instantVelocity * 0.6;
    }

    // store current pointer position and timestamp for the next event
    lastPointerXRef.current = e.clientX;
    lastMoveTimestamp.current = now;
  }, [containerRef]);
  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);
  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    isDraggingRef
  };
};